import { lazy, Suspense, useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { trpc, createTRPCClient } from '@/lib/trpc'
import { SkillsPage } from '@/pages/SkillsPage'
import { SkillDetailPage } from '@/pages/SkillDetailPage'
import { MediaViewerPage } from '@/pages/MediaViewerPage'
import { LoginPage } from '@/pages/LoginPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { AuthenticatedLayout } from '@/components/AuthenticatedLayout'

// Visual/motion labs run on fake data — DEV only, and lazy so production never ships them
const LabsRoutes = import.meta.env.DEV ? lazy(() => import('@/labs/LabsRoutes')) : null

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Root path goes straight to the skills list (or login) — no dev landing page.
function RootRedirect() {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return null
  return <Navigate to={isAuthenticated ? '/skills' : '/login'} replace />
}

function App() {
  const [trpcClient] = useState(() => createTRPCClient())

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/skills"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout>
                      <SkillsPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/skills/:id"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout>
                      <SkillDetailPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/skills/:id/:mediaId"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout>
                      <MediaViewerPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout>
                      <SettingsPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              {LabsRoutes && (
                <Route
                  path="/labs/*"
                  element={
                    <Suspense fallback={null}>
                      <LabsRoutes />
                    </Suspense>
                  }
                />
              )}
              <Route path="*" element={<RootRedirect />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </trpc.Provider>
  )
}

export default App
